/**
 * US state / territory code <-> name mapping.
 *
 * Used to join registrant contact states (2-letter codes from RDDS) against
 * FDIC institution states (full STNAME strings, e.g. "Texas"). Includes the
 * territories that appear in both FDIC data and registrant addresses, plus
 * aliases for FDIC naming variants.
 */

export const US_STATE_NAMES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  // Territories
  AS: 'American Samoa',
  FM: 'Federated States of Micronesia',
  GU: 'Guam',
  MH: 'Marshall Islands',
  MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico',
  PW: 'Palau',
  VI: 'U.S. Virgin Islands',
};

// Normalized (lowercase) full name -> code, including FDIC naming variants
const NAME_TO_CODE: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(US_STATE_NAMES).map(([code, name]) => [name.toLowerCase(), code]),
  ),
  // FDIC STNAME variants
  'virgin islands of the u.s.': 'VI',
  'virgin islands': 'VI',
  'district of columbia': 'DC',
  'micronesia': 'FM',
};

/** Map a full state name (e.g. FDIC STNAME "Texas") to its 2-letter code. */
export function stateNameToCode(name: string | null | undefined): string | null {
  if (!name)
    return null;
  return NAME_TO_CODE[name.trim().toLowerCase()] ?? null;
}

/** Map a 2-letter code to its display name (falls back to the code itself). */
export function stateCodeToName(code: string | null | undefined): string | null {
  if (!code)
    return null;
  return US_STATE_NAMES[code.trim().toUpperCase()] ?? code.trim().toUpperCase();
}

/** Is this a known US state/territory code? */
export function isKnownStateCode(code: string | null | undefined): boolean {
  return !!code && code.trim().toUpperCase() in US_STATE_NAMES;
}
